import PropTypes from "prop-types";
import { useMemo, useState, useEffect, useContext } from "react";
import { useStaticQuery, graphql } from "gatsby";

import { cn } from "@helpers/general";
import { getAllUrlParams } from "@helpers/browser";
import { setGorgiasChatVisibility } from "@helpers/gorgias";
import { HiddenElementsContext } from "@context/HiddenElements";

import useDiscountsInfo from "@hooks/useDiscountsInfo";
import useResponsiveWithHydrationFix from "@hooks/useResponsiveWithHydrationFix";
import { ComponentType } from "@constants/ComponentType";
import useDeviceDetect from "@hooks/useDeviceDetect";
import useManyCheckInventory from "@hooks/useManyCheckInventory";
import { useProductPricing } from "@hooks/usePrices";

import DISCOUNT_TYPES from "@constants/DiscountTypes";

import GiftProduct from "@components/giftWithPurchaseBlock/GiftProduct";
import ArrowIcon from "@svgs/arrow.svg";

function GiftWithPurchaseBlock({ componentType, bottomOffset, showFloatingElements }) {
  const { isMobile } = useDeviceDetect();
  const discountsInfo = useDiscountsInfo();

  const { hide, show } = useContext(HiddenElementsContext);

  const [showDetails, setShowDetails] = useState(false);

  const data = useStaticQuery(graphql`
    query {
      contentfulGiftWithPurchaseBlock {
        blockMessage
        title
        subtitle
        tagLabel
        backgroundColor {
          mobile
          desktop
        }
        blockMessageBackgroundColor {
          mobile
          desktop
        }
        blockMessageTextColor {
          mobile
          desktop
        }
        titleColor {
          desktop
          mobile
        }
        subtitleColor {
          desktop
          mobile
        }
        tagTextColor {
          desktop
          mobile
        }
      }
    }
  `);

  const {
    blockMessage,
    title,
    subtitle,
    tagLabel,
    blockMessageBackgroundColor,
    backgroundColor,
    blockMessageTextColor,
    titleColor,
    subtitleColor,
    tagTextColor,
  } = data.contentfulGiftWithPurchaseBlock || {};

  const giftDiscount =
    (discountsInfo || []).find((s) => s.type === DISCOUNT_TYPES.GIFT_WITH_PURCHASE) || null;

  const freeGiftProducts = useMemo(() => giftDiscount?.freegift?.products || [], [giftDiscount]);

  const freeGiftProductsInventory = useManyCheckInventory(freeGiftProducts);

  const onlyOneAvailableGift = freeGiftProducts.length === 1;
  const messageBackgroundColor = useResponsiveWithHydrationFix(
    blockMessageBackgroundColor.mobile,
    blockMessageBackgroundColor.desktop
  );

  const blockBackgroundColor = useResponsiveWithHydrationFix(
    backgroundColor.mobile,
    backgroundColor.desktop
  );

  const messageTextColor = useResponsiveWithHydrationFix(
    blockMessageTextColor.mobile,
    blockMessageTextColor.desktop
  );

  const titleTextColor = useResponsiveWithHydrationFix(titleColor.mobile, titleColor.desktop);

  const subtitleTextColor = useResponsiveWithHydrationFix(
    subtitleColor.mobile,
    subtitleColor.desktop
  );
  const tagColor = useResponsiveWithHydrationFix(tagTextColor.mobile, tagTextColor.desktop);

  useEffect(() => {
    const urlParams = getAllUrlParams();
    const { gwp } = urlParams || {};
    if (gwp === "true") {
      setShowDetails(true);
    }
  }, []);

  const shouldShowBlock =
    isMobile && componentType === ComponentType.PDP ? showFloatingElements : true;

  const { products: freeGiftProductsWithPrices } = useProductPricing({
    products: freeGiftProducts.map((p) => ({ ...p.shopify, handle: p.handle })),
  });

  const orderedProducts = useMemo(
    () =>
      freeGiftProducts
        .map((p, i) => ({ ...p, prices: freeGiftProductsWithPrices[i] }))
        .sort((a, b) => {
          const inventoryA = freeGiftProductsInventory.find((inv) => inv.handle === a.handle);
          const inventoryB = freeGiftProductsInventory.find((inv) => inv.handle === b.handle);
          if (inventoryA?.availableForSale && !inventoryB?.availableForSale) return -1;
          if (!inventoryA?.availableForSale && inventoryB?.availableForSale) return 1;
          return 0;
        }),
    [freeGiftProducts, freeGiftProductsWithPrices, freeGiftProductsInventory]
  );

  if (blockMessage === "null") {
    return null;
  }

  let arrowStyle = "";
  if (isMobile) {
    arrowStyle = showDetails ? "-rotate-90" : "rotate-90";
  } else {
    arrowStyle = showDetails ? "rotate-90" : "-rotate-90";
  }

  let bottomOffsetValue = "unset";
  if (isMobile && componentType === ComponentType.PDP) {
    bottomOffsetValue = `${bottomOffset}px`;
  }
  if (isMobile && componentType === ComponentType.CP) {
    bottomOffsetValue = `0px`;
  }

  const handleBlockMessageClick = () => {
    if (componentType === ComponentType.CP && isMobile) {
      setGorgiasChatVisibility(showDetails);
      if (showDetails) {
        show();
      } else {
        hide();
      }
    }
    setShowDetails(!showDetails);
  };

  if (!shouldShowBlock || !giftDiscount) {
    return null;
  }

  return (
    <div
      style={{
        bottom: bottomOffsetValue,
      }}
      className={cn("fixed w-full z-[100] top-auto desktop:right-[25px]", {
        "w-[400px] h-[45px] absolute z-1": !isMobile,
      })}
    >
      <button
        type="button"
        className="flex justify-start desktop:justify-between items-center px-[15px] desktop:px-[20px] py-[13px] w-full desktop:border desktop:border-dark-grey"
        style={{
          backgroundColor: messageBackgroundColor,
          color: messageTextColor,
          borderRadius: !isMobile && !showDetails ? "0px 0px 8px 8px" : "0px",
        }}
        onClick={handleBlockMessageClick}
      >
        <p className="typo-h3-bold desktop:typo-body-bold mobile:mr-[14px]">{blockMessage}</p>
        <ArrowIcon width="7" height="15" fill={messageTextColor} className={arrowStyle} />
      </button>
      {showDetails && (
        <div
          className={cn(
            "p-[15px] desktop:px-[20px] desktop:border desktop:border-dark-grey desktop:rounded-br-[8px] desktop:rounded-bl-[8px] desktop:border-t-0",
            {
              "flex items-center justify-between pt-[20px] desktop:px-[15px]": onlyOneAvailableGift,
            }
          )}
          style={{ backgroundColor: blockBackgroundColor }}
        >
          <div>
            <p className="title-m desktop:title-xs" style={{ color: titleTextColor }}>
              {title}
            </p>
            <p
              className="typo-body pt-[8px] desktop:typo-micro"
              style={{ color: subtitleTextColor }}
            >
              {subtitle}
            </p>
          </div>

          <div
            className={cn("flex justify-between w-full gap-[10px] shrink-0", {
              "w-auto": onlyOneAvailableGift,
              "mt-[6px] desktop:mt-[30px]": !onlyOneAvailableGift,
            })}
          >
            {orderedProducts.map((product) => (
              <GiftProduct
                key={product.handle}
                product={product}
                onlyOneAvailableGift={onlyOneAvailableGift}
                tagLabel={tagLabel}
                textColor={tagColor}
                noOfProducts={freeGiftProducts.length}
                isAvailable={
                  freeGiftProductsInventory.find((p) => p.handle === product.handle)
                    ?.availableForSale
                }
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

GiftWithPurchaseBlock.propTypes = {
  componentType: PropTypes.string,
  bottomOffset: PropTypes.number,
  showFloatingElements: PropTypes.bool,
};

export default GiftWithPurchaseBlock;
